import { useContext, useEffect, useState } from "react";

import { Flag, TypedEvent } from "../../utils/events";
import { sortBy, throttle } from "../../utils/functional";
import { NavMenuRefsContext } from "./context";

export interface ScrollNavItem {
    elemID: string;
    name: string;
    sortOrder: number;
}

type NavAnchorTarget = {
    link: HTMLAnchorElement;
    hashFragment: string;
    target: HTMLElement;
    nextTarget: HTMLElement | null;
};

export const navMenuReady = new Flag();
export const onRegisterMenuItem = new TypedEvent<ScrollNavItem>();
export const onNavItemClicked = new TypedEvent<ScrollNavItem>();

// Maintain a global memo of the items. This is required for supporting multiple
// user's of the useMenuItems hook.
let navItemsMemo: readonly ScrollNavItem[] = [];

/**
 * Get the top (in pixels) of the given scroll nav anchor target.
 */
const getTopThreshold = ({ target }: { target: HTMLElement }): number => {
    return target.offsetTop;
};

/**
 * Given an anchor link, find the target element.
 */
const getTargetForLink = (
    link: HTMLAnchorElement,
): [HTMLElement | null, string | null] => {
    const hashFragment = link.getAttribute("href");
    const target = hashFragment
        ? document.querySelector<HTMLElement>(hashFragment)
        : null;
    return [target, hashFragment];
};

/**
 * Based on the current scroll position of the window, figure out which nav item
 * should be marked as "active". Active is defined as the first linked to section
 * that that is visible in the viewport.
 */
const findActiveNavItem = ({
    scrollPosFromTop,
    linksUL,
}: {
    scrollPosFromTop: number;
    linksUL: HTMLUListElement;
}): NavAnchorTarget | null => {
    // Find all of the nav <li> elements
    const links = [
        ...linksUL.querySelectorAll<HTMLAnchorElement>("li > a[href]"),
    ];
    const activeNavItem = links
        // Build up a list of all of the links in the nav menu, correlated with
        // the section element they refer to.
        .map((link) => {
            const [target, hashFragment] = getTargetForLink(link);
            return {
                link,
                hashFragment,
                target,
            };
        })
        // For each target, add in a look-ahead reference to the next target.
        // This provides the bottom threshold of when to no longer consider a
        // section active.
        .map((data, i, arr) => {
            const next = arr[i + 1] as (typeof arr)[number] | undefined;
            return {
                ...data,
                nextTarget: next?.target || null,
            };
        })
        // Filter out links with an href that don't match a page element.
        .filter((data): data is NavAnchorTarget => {
            return !!(data.hashFragment && data.target);
        })
        // Of the remaining links, find the first one who's related section
        // element is within the viewport.
        .find(({ target, nextTarget }) => {
            const minScrollPos = getTopThreshold({
                target,
            });
            const maxScrollPos = nextTarget
                ? getTopThreshold({
                      target: nextTarget,
                  })
                : Infinity;
            return (
                scrollPosFromTop >= minScrollPos &&
                scrollPosFromTop < maxScrollPos
            );
        });
    return activeNavItem ?? null;
};

/**
 * Scroll the window to the target of the given anchor link.
 */
export const scrollToLinkTarget = (link: HTMLAnchorElement): void => {
    // Find the anchor target
    const [target, hashFragment] = getTargetForLink(link);
    if (!target || !hashFragment) {
        return;
    }
    // Scroll to the target
    const scrollTop = getTopThreshold({ target });
    window.scrollTo({
        left: 0,
        // Subtract menu item link height because we don't want the sticky nav
        // to cover the top of the section.
        // ASSUMPTION: ScrollNavMenuBlock has position: sticky on it
        // ASSUMPTION: link is as tall as the nav as a whole
        top: scrollTop - link.clientHeight,
        behavior: "smooth",
    });
};

/**
 * React hook used to access all of the scroll nav anchor targets which are
 * rendered on the current page.
 */
export const useMenuItems = (): readonly ScrollNavItem[] => {
    const [navItems, setNavItems] = useState<readonly ScrollNavItem[]>([
        ...navItemsMemo,
    ]);

    const registerMenuItem = (newItem: ScrollNavItem) => {
        setNavItems((prevItems) => {
            // Do nothing if this element already exists.
            const elemIDs = new Set(prevItems.map((item) => item.elemID));
            if (elemIDs.has(newItem.elemID)) {
                return prevItems;
            }
            // Add thew new item and resort everything by sortOrder
            const newItems = sortBy(
                [...prevItems].concat(newItem),
                "sortOrder",
            );
            // Update the global nav items memo.
            navItemsMemo = newItems;
            return newItems;
        });
    };

    useEffect(() => {
        // Register a event handler to update the component state whenever a new
        // menu item it registered.
        onRegisterMenuItem.on(registerMenuItem);
        // Set the flag navMenuReady to indicate to menu item components that
        // they may begin registering themselves.
        navMenuReady.set();
        return () => {
            onRegisterMenuItem.off(registerMenuItem);
        };
    }, []);

    return navItems;
};

/**
 * React hook used to access the active nav item (based on the current scroll
 * position)
 */
export const useActiveNavItemHref = (): string | null => {
    const { fullBleedRef, navLinksRef } = useContext(NavMenuRefsContext);
    const [activeNavItem, setActiveNavItem] = useState<string | null>("");
    const onScroll = throttle(() => {
        if (!navLinksRef?.current || !fullBleedRef?.current) {
            return;
        }
        const active = findActiveNavItem({
            // Adding nav height because we don't want the sticky nav
            // to cover the top of the section.
            // ASSUMPTION: Nav has position: sticky on it
            scrollPosFromTop:
                window.scrollY + fullBleedRef.current.clientHeight,
            linksUL: navLinksRef.current,
        });
        setActiveNavItem(active ? active.hashFragment : null);
    }, 100);

    // Attach the event handler to update the active item whenever the user
    // scrolls the window.
    useEffect(() => {
        window.addEventListener("scroll", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    // Attach an effect to update the URL's hash fragment when the active nav item changes
    useEffect(() => {
        // Update the URL hash fragment
        const newURL = activeNavItem
            ? new URL(activeNavItem, window.location.href)
            : new URL("", window.location.href);
        window.history.replaceState(null, "", newURL);
    }, [activeNavItem]);

    // Return the active nav item
    return activeNavItem;
};

/**
 * Helper function for generating the link href value for a given nav item.
 */
export const getNavItemHref = (item: ScrollNavItem): string => {
    return `#${item.elemID}`;
};

/**
 * React hook used to access the active nav item (based on the current scroll
 * position)
 */
export const useActiveNavItem = (): ScrollNavItem | null => {
    const navItems = useMenuItems();
    const activeNavItemHref = useActiveNavItemHref();
    const activeItem = navItems.find((navItem) => {
        return getNavItemHref(navItem) === activeNavItemHref;
    });
    return activeItem || null;
};
